import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from 'api/axios';

const WsContext = createContext(null);

const UNREAD_LS_KEY = 'chat_unreadByKey_v1';
const LATEST_LS_KEY = 'chat_latestByKey_v1';

function extractPreview(raw) {
  const m = raw?.message || {};
  const txt = m.body ?? m.text ?? m.caption ?? raw?.message ?? raw?.text ?? '';
  if (typeof txt === 'string' && txt.trim()) return txt.trim();
  return '';
}

function safeParseLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : fallback;
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function safeSetLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function normalizeWsMessage(raw) {
  const sessionName = raw?.sessionName || '';
  const direction = raw?.direction === 'outgoing' ? 'outgoing' : 'incoming';
  const m = raw?.message || {};

  const messageId = m.id || raw?.messageId || '';
  const from = m.from || raw?.fromWaId || '';
  const to = m.to || raw?.toNumber || '';

  const chatId = direction === 'incoming' ? from : to;

  const tsSec = typeof m.timestamp === 'number' ? m.timestamp : typeof m.t === 'number' ? m.t : null;
  const createdAt = raw?.createdAt || m.createdAt || (tsSec ? new Date(tsSec * 1000).toISOString() : new Date().toISOString());

  return {
    id: raw?.id || messageId || Date.now(),
    messageId,
    sessionName,
    chatId,
    fromWaId: from,
    toNumber: to,
    direction,
    createdAt,
    raw
  };
}

export function WsProvider({ children }) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  const [isConnected, setIsConnected] = useState(false);

  const [unreadByKey, setUnreadByKey] = useState(() => safeParseLS(UNREAD_LS_KEY, {}));
  const [latestByKey, setLatestByKey] = useState(() => safeParseLS(LATEST_LS_KEY, {}));

  // LID -> c.us cache (in-memory)
  const lidMapRef = useRef(new Map());

  const WS_URL = useMemo(() => {
    const host = window.location.hostname;
    return `ws://${host}:3000/ws/messages`;
  }, []);

  useEffect(() => safeSetLS(UNREAD_LS_KEY, unreadByKey || {}), [unreadByKey]);
  useEffect(() => safeSetLS(LATEST_LS_KEY, latestByKey || {}), [latestByKey]);

  const resolveChatId = useCallback(async (sessionName, wsChatId) => {
    if (!sessionName || !wsChatId) return null;

    const cacheKey = `${sessionName}|${wsChatId}`;
    const cached = lidMapRef.current.get(cacheKey);
    if (cached) return cached;

    try {
      const ss = encodeURIComponent(sessionName);
      const digits = String(wsChatId).replace('@lid', '').replace('@c.us', '');
      const chatIdParam = encodeURIComponent(digits);

      const res = await api.get(`/api/messages/chats?sessionName=${ss}&chatId=${chatIdParam}`, {
        withCredentials: true,
        headers: { 'X-API-Key': 'secret123' }
      });

      const payload = res?.data || {};
      const list = Array.isArray(payload.chats) ? payload.chats : [];
      const found = list.find((x) => String(x?.chatId || '').includes('@c.us')) || list[0];
      const resolved = found?.chatId ? String(found.chatId) : null;

      if (resolved) lidMapRef.current.set(cacheKey, resolved);
      return resolved;
    } catch {
      return null;
    }
  }, []);

  const normalizeChatId = useCallback(
    async (sessionName, chatId) => {
      if (!sessionName || !chatId) return null;
      if (!String(chatId).includes('@lid')) return String(chatId);

      const resolved = await resolveChatId(sessionName, chatId);
      return resolved || null;
    },
    [resolveChatId]
  );

  const markRead = useCallback((sessionName, chatId) => {
    if (!sessionName || !chatId) return;
    const key = `${sessionName}|${chatId}`;
    setUnreadByKey((prev) => ({ ...(prev || {}), [key]: 0 }));
  }, []);

  const clearAllUnread = useCallback(() => {
    setUnreadByKey((prev) => {
      const next = { ...(prev || {}) };
      for (const k of Object.keys(next)) next[k] = 0;
      return next;
    });
  }, []);

  // ✅ FIX: dependency array TIDAK BOLEH include "connect" itself
  const connect = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      setIsConnected(true);
    };

    ws.onmessage = async (evt) => {
      let raw;
      try {
        raw = JSON.parse(evt.data);
      } catch {
        return;
      }
      if (raw?.type === 'connected') return;

      // 1) unread event
      if (raw?.type === 'chat_unread') {
        const sessionName = raw?.sessionName || '';
        const wsChatId = raw?.chatId || '';
        const unread = Number(raw?.unread || 0);
        if (!sessionName || !wsChatId) return;

        const effectiveChatId = await normalizeChatId(sessionName, wsChatId);
        if (!effectiveChatId) return;

        const key = `${sessionName}|${effectiveChatId}`;

        setUnreadByKey((prev) => {
          const cur = Number(prev?.[key] || 0);
          const nextVal = Math.max(cur, unread);
          return { ...(prev || {}), [key]: nextVal };
        });

        setLatestByKey((prev) => {
          const next = { ...(prev || {}) };
          next[key] = {
            sessionName,
            chatId: effectiveChatId,
            preview: next[key]?.preview || '',
            ts: Date.now()
          };
          return next;
        });

        return;
      }

      // 2) message event
      const msg = normalizeWsMessage(raw);
      if (!msg.sessionName || !msg.chatId) return;

      let effectiveChatId = msg.chatId;
      if (String(effectiveChatId).includes('@lid')) {
        const resolved = await normalizeChatId(msg.sessionName, effectiveChatId);
        if (!resolved) return;
        effectiveChatId = resolved;
      }

      const key = `${msg.sessionName}|${effectiveChatId}`;
      const preview = extractPreview(raw);

      setLatestByKey((prev) => {
        const next = { ...(prev || {}) };
        next[key] = {
          sessionName: msg.sessionName,
          chatId: effectiveChatId,
          preview: preview || next[key]?.preview || '',
          ts: Date.now()
        };
        return next;
      });

      if (msg.direction === 'incoming') {
        setUnreadByKey((prev) => ({ ...(prev || {}), [key]: Number(prev?.[key] || 0) + 1 }));
      }
    };

    ws.onclose = () => {
      setIsConnected(false);

      const retry = retryRef.current + 1;
      retryRef.current = retry;
      const delay = Math.min(1000 * Math.pow(2, Math.min(retry, 5)), 15000);

      setTimeout(() => {
        connect(); // ✅ aman setelah initialized
      }, delay);
    };

    ws.onerror = () => {
      // onclose handles retry
    };
  }, [WS_URL, normalizeChatId]); // ✅ ONLY deps that are actually used

  useEffect(() => {
    connect();
    return () => {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
  }, [connect]);

  const value = useMemo(
    () => ({
      isConnected,
      unreadByKey,
      latestByKey,
      markRead,
      clearAllUnread
    }),
    [isConnected, unreadByKey, latestByKey, markRead, clearAllUnread]
  );

  return <WsContext.Provider value={value}>{children}</WsContext.Provider>;
}

export function useWs() {
  const ctx = useContext(WsContext);
  if (!ctx) throw new Error('useWs must be used inside <WsProvider />');
  return ctx;
}
